/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { Card, Form, Col, Collapse } from "react-bootstrap";
import { getAllCannedResponseCategories } from "../../services/CannedResponseService";
import { SendResponse } from "./SendResponse";
import useSendEvents from "../../hooks/useSendEvents";
import { Typeahead } from "react-bootstrap-typeahead";
import RichTextField from "../../../../components/RichTextField/RichTextField";

/**
 * This component fetches and displays the canned response categories in the Canned Responses CCP tab.
 * This responses can be copied to the clipboard or sent directly into the chat.
 */
const CannedResponses = ({ contact, placeholder }) => {
	const [categories, setCategories] = React.useState([]);
	const [searchTerm, setSearchTerm] = React.useState("");
	const [searchMatches, setSearchMatches] = React.useState([]);
	const [open, setOpen] = React.useState({});
	const [tagOptions, setTagOptions] = React.useState([]);
	const [selectedTags, setSelectedTags] = React.useState({});
	const sendMessage = useSendEvents();

	React.useEffect(() => {
		const newSearchMatches = [];
		const newOpen = {};
		if (searchTerm || Object.keys(selectedTags).length > 0) {
			categories.forEach((category) => {
				if (searchTerm && category.categoryName.toLowerCase().includes(searchTerm.toLowerCase())) {
					newSearchMatches.push(category);
					newOpen[category.categoryName] = true;
				} else {
					const matchingResponses = [];
					category.responses.forEach((response) => {
						if (
							(response.responseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
								response.message.toLowerCase().includes(searchTerm.toLowerCase())) &&
							(Object.keys(selectedTags).length === 0 || response.tags.find((tag) => selectedTags[tag]))
						) {
							matchingResponses.push(response);
						}
					});
					if (matchingResponses.length > 0) {
						newSearchMatches.push({ ...category, responses: matchingResponses });
						newOpen[category.categoryName] = true;
					}
				}
			});
			setOpen((oldOpen) => ({ ...oldOpen, ...newOpen }));
			setSearchMatches(newSearchMatches);
		} else {
			setSearchMatches(categories);
		}
	}, [searchTerm, categories, selectedTags]);

	React.useEffect(() => {
		const loadResponses = async () => {
			const data = await getAllCannedResponseCategories();
			setCategories(data);
			const newTags = {};
			const newOpen = {};
			data.forEach((category) => {
				newOpen[category.categoryName] = true;
				category.responses.forEach((response) => response.tags.forEach((tag) => (newTags[tag] = true)));
			});
			setOpen(newOpen);
			setTagOptions(Object.keys(newTags).sort((a, b) => (a > b ? 1 : -1)));
		};

		loadResponses();
	}, []);

	return contact ? (
		<div style={{ padding: "10px" }}>
			<Form>
				<Form.Row>
					<Col xl={6} lg={12}>
						<Form.Group>
							<Form.Label>Search by message, category or name</Form.Label>
							<Form.Control
								autoComplete="new-password" // prevents showing auto fill dropdown in Chrome
								type="text"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
							/>
						</Form.Group>
					</Col>
					<Col style={{ primary: "black" }} xl={6} lg={12}>
						<Form.Group>
							<Form.Label>Search by tag</Form.Label>
							<Typeahead
								id="tag-search"
								multiple
								onChange={(selected) => {
									const newSelected = {};
									selected.forEach((selection) => (newSelected[selection] = true));
									setSelectedTags(newSelected);
								}}
								options={tagOptions}
								selected={Object.keys(selectedTags)}
							/>
						</Form.Group>
					</Col>
				</Form.Row>
			</Form>
			<div style={{}}>
				{searchMatches.length > 0 ? (
					searchMatches.map((category) => (
						<Card style={{ margin: "10px 0px 10px 0px" }} key={category.categoryId}>
							<Card.Header
								onClick={() => {
									setOpen({ ...open, [category.categoryName]: !open[category.categoryName] });
								}}
								style={{ paddingRight: "10px", paddingTop: "5px" }}
								eventkey={category.categoryId}
							>
								<span style={{ paddingLeft: "15px", display: "flex", justifyContent: "space-between" }}>
									<span>
										<h5 style={{ paddingTop: "5px", fontWeight: 1000 }}>{category.categoryName}</h5>
									</span>
									<span>
										{open[category.categoryName] ? (
											<i className="fas fa-angle-up"></i>
										) : (
											<i className="fas fa-angle-down"></i>
										)}
									</span>
								</span>
							</Card.Header>
							<Collapse in={open[category.categoryName]} appear={false}>
								<div>
									<Card.Body>
										{category.responses.map((response) => (
											<Card key={response.responseId} style={{ padding: "8px", marginBottom: "5px" }}>
												<div>
													<div style={{ display: "flex", justifyContent: "space-between" }}>
														<div>
															<label style={{ fontWeight: 600, marginRight: "5px" }}>Response name: </label>
															<span style={{ wordBreak: "break-all" }}>{response.responseName}</span>
														</div>
														<SendResponse contact={contact} sendMessage={sendMessage} value={response.message} />
													</div>
													<div>
														<label style={{ fontWeight: 600, marginRight: "5px" }}>Tags: </label>
														<span>{response.tags.join(", ")}</span>
													</div>
												</div>
												<div>
													<div>
														<label style={{ fontWeight: 600, marginRight: "5px" }}>Message: </label>
														<RichTextField
															height="auto"
															maxHeight={200}
															value={response.message}
															extensions={{
																bold: true,
																italic: true,
																bulletList: true,
																orderedList: true,
																history: true,
															}}
															overrideExtensions
															readOnly
														/>
													</div>
												</div>
											</Card>
										))}
									</Card.Body>
								</div>
							</Collapse>
						</Card>
					))
				) : (
					<div style={{ fontWeight: "bold", margin: "auto", textAlign: "center" }}>No categories found.</div>
				)}
			</div>
		</div>
	) : (
		placeholder
	);
};
export default CannedResponses;
